import { Link } from '@tanstack/react-router';
import type { ArticleMineItemDto } from '@zoxo/shared';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { EmptyState } from '../../components/ui/empty-state';
import { ListTail } from '../../components/ui/list-tail';
import { useIntersection } from '../../hooks/use-intersection';
import { formatDate } from './format';
import { useMyArticles } from './use-my-articles';
import { Newspaper } from '@/components/icons';

/**
 * "내 기사" — the viewer's own PD 수첩 articles INCLUDING pending ones (V4 §C).
 * Entry point so an author can find and edit a not-yet-approved submission that
 * the public /articles feed hides. Reuses the blog-card layout; adds a status badge
 * (검수 대기 / 공개) and a direct "수정" shortcut. Each card links to the
 * (author-visible) detail. Anonymity: the viewer is the author, so no byline.
 */
export function MyArticleList() {
  const {
    data,
    error,
    isPending,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMyArticles();

  const sentinelRef = useIntersection(
    () => fetchNextPage(),
    hasNextPage && !isFetchingNextPage,
  );

  if (isPending) {
    return <p className="text-sm text-text-muted">불러오는 중…</p>;
  }

  if (isError) {
    return (
      <div role="alert" className="py-8 text-center">
        <p className="mb-4 text-sm text-text">
          기사를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
        </p>
        <Button variant="secondary" onClick={() => void fetchNextPage()}>
          다시 시도
        </Button>
        <span hidden>{error.message}</span>
      </div>
    );
  }

  const articles = data.pages.flatMap((p) => p.data);

  if (articles.length === 0) {
    return (
      <EmptyState
        icon={<Newspaper />}
        title="아직 투고한 기사가 없어요."
        description="우리 일터에서 짚어야 할 이야기가 있다면 아티클로 남겨 보세요."
        action={
          <Button asChild variant="primary">
            <Link to="/articles/create">글쓰기</Link>
          </Button>
        }
      />
    );
  }

  return (
    <>
      <ul className="divide-y divide-border-subtle">
        {articles.map((article) => (
          <MyArticleCard key={article.id} article={article} />
        ))}
      </ul>
      <div ref={sentinelRef} aria-hidden="true" />
      <ListTail fetching={isFetchingNextPage} done={!hasNextPage} />
    </>
  );
}

const STATUS_BADGE = {
  draft: { label: '초안', variant: 'neutral' },
  pending: { label: '검수 대기', variant: 'warning' },
  published: { label: '공개', variant: 'success' },
} as const

export function MyArticleCard({ article }: { article: ArticleMineItemDto }) {
  const badge = STATUS_BADGE[article.status]
  /*
    카드 전체를 <a> 로 감싸지 않는다 — 「수정」이 태그 줄 아래, 본문 열 안에 있어야 하는데
    <a> 안에 <a> 는 안 된다. 그림과 제목이 상세로 가는 링크고, 수정은 그 아래 글자 링크다.
  */
  return (
    <li className="flex gap-3 py-4">
      {article.thumbnailUrl && (
        <Link
          to="/articles/$id"
          params={{ id: article.id }}
          className="group aspect-[4/3] w-1/3 shrink-0 overflow-hidden rounded-md bg-surface-muted"
        >
          <img
            src={article.thumbnailUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </Link>
      )}
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-center gap-2">
          <Badge variant={badge.variant}>{badge.label}</Badge>
          <span className="text-xs text-text-subtle">
            {formatDate(article.createdAt)}
          </span>
        </div>
        <h2 className="mb-1 line-clamp-2 text-base font-bold leading-snug text-text">
          <Link to="/articles/$id" params={{ id: article.id }} className="hover:underline underline-offset-2">
            {article.title}
          </Link>
        </h2>
        {article.summary && (
          <p className="mb-1 line-clamp-2 text-sm leading-relaxed text-text-muted">
            {article.summary}
          </p>
        )}
        {article.tags && article.tags.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {article.tags.map((t) => (
              <li
                key={t}
                className="rounded-md bg-brand-soft px-2 py-0.5 text-xs font-medium text-brand-strong"
              >
                #{t}
              </li>
            ))}
          </ul>
        )}
        {/* 상세와 같은 자리·같은 모양: 태그 줄 아래의 글자 버튼. */}
        <div className="mt-2">
          <Link
            to="/articles/$id/edit"
            params={{ id: article.id }}
            className="text-xs font-medium text-text-muted hover:text-text hover:underline underline-offset-2"
          >
            수정
          </Link>
        </div>
      </div>
    </li>
  );
}
